/**
 * ⚡ 긴 목록을 「앞부분 먼저 · 나머지는 한가할 때 이어 붙이기」로 그린다 (v2.222.2 주소록 → v2.222.3 공용으로 뺌)
 *
 * 왜: 거래처 담당자 591줄 · 거래처 772곳을 한 번에 그리면 표 배치·그리기에 화면이 0.5~1초 멈췄다
 *   (헤드리스 실측 — 글자 계산보다 표 배치·그리기가 대부분. 사용자 「처음 목록 나올 때 느려」).
 *
 * 어떻게: 첫 `first` 줄만 먼저 그리고, 나머지는 브라우저가 한가할 때(requestIdleCallback · 사파리는 setTimeout)
 *   `chunk` 줄씩 이어 붙인다. ⭐다 붙고 나면 **전체 줄이 화면에 있다** — Ctrl+F 찾기·스크롤은 종전과 같다.
 *   건수·엑셀 내려받기는 부르는 쪽이 원래 배열(`rows`)로 센다 — 이 훅은 「그리는 줄」만 줄인다.
 *
 * `listKey` — 「지금 어떤 목록을 보고 있나」(검색·필터·정렬). 바뀌면 **그리는 그 순간에** 첫 줄부터로 되돌린다.
 *   ⚠효과(useEffect)로 되돌리면 한 번은 옛 개수(전체)로 먼저 그려 버려 검색을 지울 때마다 멈춤이 되살아난다.
 *   자료만 새로 받은 것(고치고 저장 → 목록 새로 고침)은 열쇠가 같아 그리던 개수를 그대로 둔다(스크롤이 튀지 않게).
 */
import { useEffect, useMemo, useState } from 'react';

type IdleWindow = Window & {
  requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
  cancelIdleCallback?: (id: number) => void;
};

export interface ProgressiveRows<T> {
  /** 지금 그릴 줄 (앞에서부터) */
  drawnRows: T[];
  /** 아직 안 그린 줄 수 — 0 이면 전부 그렸다 */
  pendingRows: number;
}

export function useProgressiveRows<T>(
  rows: T[],
  listKey: string,
  opts: { first?: number; chunk?: number } = {},
): ProgressiveRows<T> {
  const first = opts.first ?? 80;
  const chunk = opts.chunk ?? 120;
  const [shown, setShown] = useState<{ key: string; n: number }>({ key: listKey, n: first });
  // 그리는 중에 열쇠가 바뀌었으면 곧바로 첫 줄부터 (React 가 커밋 전에 다시 그린다 — 「이전 렌더 값 맞추기」 방식)
  if (shown.key !== listKey) setShown({ key: listKey, n: first });
  const shownN = shown.key === listKey ? shown.n : first;
  const drawnRows = useMemo(() => (shownN >= rows.length ? rows : rows.slice(0, shownN)), [rows, shownN]);
  const pendingRows = rows.length - drawnRows.length;

  useEffect(() => {
    if (pendingRows <= 0) return;
    const grow = (): void => setShown((s) => (s.key === listKey ? { key: listKey, n: s.n + chunk } : s));
    const w = window as IdleWindow;
    if (w.requestIdleCallback && w.cancelIdleCallback) {
      const id = w.requestIdleCallback(grow, { timeout: 400 });
      return () => w.cancelIdleCallback!(id);
    }
    const id = window.setTimeout(grow, 30); // 사파리 — requestIdleCallback 이 없다
    return () => window.clearTimeout(id);
  }, [pendingRows, listKey, chunk]);

  return { drawnRows, pendingRows };
}
